"use client";

import type { CandidateEducation } from "@/lib/api/types";
import { formatYearMonthShort } from "@/lib/date/date";
import { titleCasePreserveAcronyms } from "@/lib/string/title-case";
import { EducationLogo } from "@/features/candidate";
import { SectionCard } from "../section-layout";
import { HighlightedText } from "@/features/arbitrator/components/review/highlighted-text";

export function EducationSection({ education }: { education: CandidateEducation[] }) {
  if (education.length === 0) return null;

  return (
    <SectionCard title="Education">
      {education.map((edu, idx) => (
        <div key={idx} className="w-full min-w-0">
          {idx > 0 && <div className="h-px w-full bg-border" />}
          <div
            className={`flex w-full min-w-0 shrink-0 flex-col items-stretch border-x border-border bg-surface-elevated p-5 ${idx === 0 ? "border-t border-border" : ""} ${idx === education.length - 1 ? "rounded-b-md border-b border-border" : ""}`}
          >
            {/* Figma 799:1800 — row: logo + flex-[1_0_0] column; text lines w-full */}
            <div className="flex w-full min-w-0 gap-2.5 items-start">
              <EducationLogo
                size="lg"
                domain={edu.school_domain}
                name={edu.school_name}
              />
              <div
                className="flex min-h-0 min-w-0 flex-[1_0_0] flex-col gap-1 items-start justify-center"
                data-evidence-source="education"
                data-evidence-index={idx}
              >
                {edu.school_name && (
                  <p className="text-sm font-medium text-foreground tracking-[-0.14px] w-full min-w-0 break-words">
                    <HighlightedText source="education">{titleCasePreserveAcronyms(edu.school_name)}</HighlightedText>
                  </p>
                )}
                {(edu.degrees?.length > 0 || edu.majors?.length > 0) && (
                  <p className="text-[13px] font-normal text-foreground/70 tracking-[-0.13px] w-full min-w-0 break-words">
                    <HighlightedText source="education">
                      {[...(edu.degrees || []), ...(edu.majors || [])]
                        .filter(Boolean)
                        .map((s) => titleCasePreserveAcronyms(s))
                        .join(", ")}
                    </HighlightedText>
                  </p>
                )}
                {(edu.start_date || edu.end_date) && (
                  <p className="text-[13px] font-normal text-foreground/70 tracking-[-0.13px] w-full">
                    {edu.start_date
                      ? (formatYearMonthShort(edu.start_date).split(" ")[1] ??
                        formatYearMonthShort(edu.start_date))
                      : ""}
                    {edu.start_date && edu.end_date && " - "}
                    {edu.end_date
                      ? (formatYearMonthShort(edu.end_date).split(" ")[1] ??
                        formatYearMonthShort(edu.end_date))
                      : ""}
                  </p>
                )}
                {edu.gpa && (
                  <p className="text-sm font-normal text-foreground/80 tracking-[-0.14px] w-full mt-2">
                    Grade: {edu.gpa}
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      ))}
    </SectionCard>
  );
}
